// Lista de espera. Centro Medico → Agenda → Lista de espera.
// Pacientes esperando cupo; al cancelarse una cita el sistema los marca 'notificado'
// (el envío real del aviso queda pendiente de worker). Desde aquí se agenda o se quita.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  AutoTextarea,
  Campo, EsperaRow, MedicoRow, Modal, PacienteRow, Especialidad, Switch, fieldCls,
  TIPO_ATENCION, med, medRpc,
} from './shared';

const ESTADOS = ['espera', 'notificado', 'agendado', 'cancelado'];
const estadoCls: Record<string, string> = {
  espera: 'bg-pendiente-soft text-pendiente', notificado: 'bg-primary/15 text-primary',
  agendado: 'bg-estable-soft text-estable', cancelado: 'bg-muted text-muted-foreground',
};

export function ListaEspera() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [estado, setEstado] = useState('espera');
  const { data: rows = [], isFetching } = useQuery<EsperaRow[]>({
    queryKey: ['med-espera', estado],
    queryFn: () => medRpc<EsperaRow[]>('med_lista_espera', { _estado: estado }),
  });

  const [abierto, setAbierto] = useState(false);

  const quitar = useMutation({
    mutationFn: (id: string) => med.update('lista_espera', id, { estado: 'cancelado' }),
    onSuccess: () => { toast.success('Quitado de la lista'); void qc.invalidateQueries({ queryKey: ['med-espera'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const marcarAgendado = useMutation({
    mutationFn: (id: string) => med.update('lista_espera', id, { estado: 'agendado' }),
    onSuccess: () => { toast.success('Marcado como agendado'); void qc.invalidateQueries({ queryKey: ['med-espera'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Lista de espera</h1>
        <Button onClick={() => setAbierto(true)}><Plus className="w-4 h-4 mr-1" /> Agregar a la lista</Button>
      </div>

      <div className="inline-flex rounded-md border overflow-hidden">
        {ESTADOS.map((s) => (
          <button key={s} onClick={() => setEstado(s)} aria-pressed={estado === s}
            className={`px-3 py-1.5 text-sm capitalize ${estado === s ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>{s}</button>
        ))}
      </div>

      <Card>
        <CardContent className="overflow-x-auto pt-4">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Paciente</th><th className="py-2 pr-3">Médico</th><th className="py-2 pr-3">Especialidad</th>
              <th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Prioridad</th><th className="py-2 pr-3">Deseada</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin registros</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{r.paciente}<div className="text-xs text-muted-foreground">{r.paciente_tel || ''}</div></td>
                  <td className="py-2 pr-3">{r.medico || 'Cualquiera'}</td>
                  <td className="py-2 pr-3">{r.especialidad || '—'}</td>
                  <td className="py-2 pr-3 capitalize">{r.tipo_atencion}</td>
                  <td className="py-2 pr-3">{r.prioridad}</td>
                  <td className="py-2 pr-3">{r.fecha_deseada || '—'}</td>
                  <td className="py-2 pr-3"><span className={`rounded px-2 py-0.5 text-xs capitalize ${estadoCls[r.estado] ?? ''}`}>{r.estado}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {r.estado !== 'agendado' && r.estado !== 'cancelado' && (
                      <button className="text-xs text-estable hover:underline mr-3" onClick={() => marcarAgendado.mutate(r.id)}>Agendado</button>
                    )}
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Quitar" aria-label="Quitar"
                      onClick={async () => { if (!(await dlg.confirmar('¿Quitar de la lista de espera?', { peligro: true }))) return; quitar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && <AgregarEspera onClose={() => setAbierto(false)} onSaved={() => { setAbierto(false); void qc.invalidateQueries({ queryKey: ['med-espera'] }); }} />}
    </div>
  );
}

function AgregarEspera({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { data: pacientes = [] } = useQuery<PacienteRow[]>({ queryKey: ['med-pacientes', ''], queryFn: () => medRpc<PacienteRow[]>('med_pacientes', { _busqueda: '' }) });
  const { data: medicos = [] } = useQuery<MedicoRow[]>({ queryKey: ['med-medicos', ''], queryFn: () => medRpc<MedicoRow[]>('med_medicos', { _busqueda: '' }) });
  const { data: especialidades = [] } = useQuery<Especialidad[]>({ queryKey: ['med-especialidades-sel'], queryFn: () => med.list<Especialidad>('especialidad', '?select=id,nombre&deleted_at=is.null&order=nombre') });

  // Si la sesión es de un médico, se encola a su nombre (no elige). Admin/recepción ve el selector.
  const { data: miMedico } = useQuery<{ id: string; razon_social: string } | null>({
    queryKey: ['med-mi-medico'], queryFn: () => medRpc<{ id: string; razon_social: string } | null>('med_mi_medico'),
  });
  const miMedicoId = miMedico?.id ?? '';

  const [f, setF] = useState({ paciente_id: '', medico_id: '', especialidad_id: '', tipo_atencion: 'presencial', prioridad: 0, fecha_deseada: '', notas: '', urgente: false });
  useEffect(() => { if (miMedicoId) setF((x) => ({ ...x, medico_id: miMedicoId })); }, [miMedicoId]);

  const guardar = useMutation({
    mutationFn: () => med.create('lista_espera', {
      paciente_id: f.paciente_id, medico_id: f.medico_id || null, especialidad_id: f.especialidad_id || null,
      tipo_atencion: f.tipo_atencion, prioridad: f.urgente ? 10 : f.prioridad,
      fecha_deseada: f.fecha_deseada || null, notas: f.notas || null,
    }),
    onSuccess: () => { toast.success('Agregado a la lista de espera'); onSaved(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Agregar a la lista de espera" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Paciente *">
          <select className={fieldCls} value={f.paciente_id} onChange={(e) => setF({ ...f, paciente_id: e.target.value })}>
            <option value="">— Selecciona —</option>
            {pacientes.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
          </select>
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label={miMedicoId ? 'Médico' : 'Médico (opcional)'}>
            {miMedicoId ? (
              <input className={fieldCls} value={miMedico?.razon_social ?? ''} readOnly disabled title="Se toma del médico que ha iniciado sesión" />
            ) : (
              <select className={fieldCls} value={f.medico_id} onChange={(e) => setF({ ...f, medico_id: e.target.value })}>
                <option value="">Cualquiera</option>
                {medicos.map((m) => <option key={m.id} value={m.id}>{m.razon_social}</option>)}
              </select>
            )}
          </Campo>
          <Campo label="Especialidad (opcional)">
            <select className={fieldCls} value={f.especialidad_id} onChange={(e) => setF({ ...f, especialidad_id: e.target.value })}>
              <option value="">—</option>
              {especialidades.map((e2) => <option key={e2.id} value={e2.id}>{e2.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Tipo de atención">
            <select className={fieldCls} value={f.tipo_atencion} onChange={(e) => setF({ ...f, tipo_atencion: e.target.value })}>
              {TIPO_ATENCION.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Campo>
          <Campo label="Fecha deseada"><input type="date" className={fieldCls} value={f.fecha_deseada} onChange={(e) => setF({ ...f, fecha_deseada: e.target.value })} /></Campo>
        </div>
        <Campo label="Notas"><AutoTextarea value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} /></Campo>
        <Switch checked={f.urgente} onChange={(v) => setF({ ...f, urgente: v })} label="Prioridad urgente" />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} disabled={!f.paciente_id} onClick={() => guardar.mutate()}>Agregar</Button>
        </div>
      </div>
    </Modal>
  );
}
